/**
 * Validador em fluxo dos Mini Formulários.
 *
 * É um grafo pequeno que roda SEMPRE no servidor, depois da validação
 * estrutural (`validarFormulario`) e antes de qualquer gravação — resposta,
 * orçamento do carrinho, retomada do fluxo do chatbot. Se ele rejeitar, nada
 * foi persistido.
 *
 * Server-only por decisão: o grafo pode consultar dataset, API e domínio, e
 * nunca é devolvido ao navegador junto com a definição pública do formulário.
 */
import {
  CampoTipo,
  ErrosValidacao,
  MiniFormularioCampo,
} from "./miniFormularioValidacao";

/** Grafo do validador, no mesmo formato do editor de fluxo (flow-castro). */
export type ValidadorFluxo = {
  nodes: Record<string, any>;
  /** Estado completo do canvas (posições, comentários); só o editor usa. */
  canvasState?: any;
};

export type RejeicaoValidador = {
  /** Nome (chave) do campo a marcar com erro. Vazio = erro do formulário. */
  chave: string;
  motivo: string;
};

/**
 * Chave em globalVars onde as rejeições se acumulam, indexadas pelo id do nó
 * que rejeitou. Nós puros são efêmeros e podem ser reprocessados no mesmo
 * play; indexar por nó torna a gravação idempotente em vez de duplicar.
 */
export const REJEICOES_KEY = "__validador_rejeicoes";

/** Chave dos erros que não apontam para um campo existente. */
export const CHAVE_FORMULARIO = "__form";

/** Motivo usado quando o nó Rejeitar não informa um. */
const MOTIVO_PADRAO = "Valor inválido";

/**
 * Porta de cada tipo de campo que NÃO é `string` — o padrão. Assim um tipo de
 * campo novo de texto (cpf, e-mail, cnpj...) já nasce com a porta certa.
 *
 * O validador recebe os valores como o cliente os submeteu: `arquivo` ainda é
 * o id do upload e `carrinho` ainda são as linhas {codigo, quantidade}; os
 * dois só viram objeto depois do aceite, quando o servidor resolve o arquivo e
 * calcula o orçamento.
 */
const PORTA_POR_TIPO: Partial<Record<CampoTipo, string>> = {
  number: "number",
  boolean: "boolean",
  date: "datetime",
  datetime: "datetime",
  agendamento: "datetime",
  multipla_escolha: "list",
  arquivo: "number",
  carrinho: "list",
};

/** Portas de saída do nó de entrada: uma por campo declarado no formulário. */
export function portasDosCampos(campos: MiniFormularioCampo[] = []) {
  return (campos ?? [])
    .filter((campo) => campo?.nome)
    .map((campo) => ({
      type: PORTA_POR_TIPO[campo.tipo] ?? "string",
      name: campo.nome,
      label: campo.label || campo.nome,
      description: `Valor enviado em "${campo.label || campo.nome}"`,
    }));
}

/** Rejeições acumuladas por um play, na ordem em que os nós rejeitaram. */
export function rejeicoesDoFluxo(
  globalVars: Record<string, any> | undefined
): RejeicaoValidador[] {
  const porNo = globalVars?.[REJEICOES_KEY];
  if (!porNo || typeof porNo !== "object") return [];

  return Object.values(porNo as Record<string, RejeicaoValidador>)
    .filter((r) => r && (r.chave || r.motivo))
    .map((r) => ({
      chave: String(r.chave ?? "").trim(),
      motivo: String(r.motivo ?? "").trim() || MOTIVO_PADRAO,
    }));
}

/**
 * Converte as rejeições no mesmo mapa `{ [campo]: mensagem }` que a validação
 * estrutural devolve — é o que a página pública já usa para marcar o campo e
 * escrever o motivo abaixo dele. Rejeição cuja chave não existe no formulário
 * vira erro geral; várias na mesma chave são empilhadas em linhas.
 */
export function errosDasRejeicoes(
  rejeicoes: RejeicaoValidador[],
  campos: MiniFormularioCampo[] = []
): ErrosValidacao {
  const nomes = new Set((campos ?? []).map((c) => c.nome));
  const erros: ErrosValidacao = {};

  for (const rejeicao of rejeicoes) {
    const chave = nomes.has(rejeicao.chave) ? rejeicao.chave : CHAVE_FORMULARIO;
    erros[chave] = erros[chave]
      ? `${erros[chave]}\n${rejeicao.motivo}`
      : rejeicao.motivo;
  }

  return erros;
}
